import { IonContent, isPlatform } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { FormInput } from '@/components/FormInput';
import { InfoProtection } from '@/components/InfoProtection';
import { UiTypography } from '@/components/ui/Typography';
import { S3_BUCKET_URL } from '@/constants';
import { useAccount } from '@/hooks/useAccount';
import { amplitudeModule } from '@/modules/amplitude';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import { useOnboarding } from '@/pages/Onboarding/context';
import { checkIsValidateEmail } from '@/pages/Onboarding/utils/checkIsValidEmail';
import { nextStepTimeout } from '@/pages/Onboarding/utils/nextStepTimeout';
import { canLogEvents } from '@/utils/canLogEvents';

import { LayoutDataType } from '../../types';

import { EmailStyles, OverlayStyles } from './Email.styles';

const ASSETS_PATH = `${S3_BUCKET_URL}/layout-own-ai-multigender-forest/email`;

export const ScreenEmail: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'Email',
      });
    }
  }, []);

  const { wizard } = useOnboarding();
  const { user } = useAccount();
  const form = useFormContext<LayoutDataType>();

  const inputRef = React.useRef<HTMLIonInputElement>(null);

  const formEmail = form.watch('email');
  const gender =
    form.watch('gender') || user?.onboarding_data.gender || 'female';
  const isValidEmail = checkIsValidateEmail(formEmail);

  React.useEffect(() => {
    if (isPlatform('android')) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.setFocus();
        }
      });
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.setFocus();
        }
      }, 1000);
    }
  }, []);

  const handleNextStep = (): void => {
    if (!formEmail || !isValidEmail) return;
    nextStepTimeout(wizard.nextStep);
  };

  const handleBack = () => {
    wizard.goToStep('chat_preview');
  };

  const bgImageSrc = `${ASSETS_PATH}/${gender}.jpg`;

  return (
    <EmailStyles
      background="dark-solid"
      bgImage={<img src={bgImageSrc} alt="Email background" />}
    >
      <OverlayStyles />
      <OnboardingNavigation onBack={handleBack} />

      <IonContent>
        <UiTypography variant="title-medium" className="email-title">
          The forest felt your presence.
          <br />A connection has begun.
        </UiTypography>

        <UiTypography
          variant="body-large"
          className="email-subtitle"
          color="neutral-white"
        >
          Leave your email to continue the journey
        </UiTypography>

        <div className="email-spacer" />

        <div className="email-input-container">
          <FormInput
            form={form}
            hidden={false}
            onSubmit={handleNextStep}
            disableButton={!isValidEmail}
            ref={inputRef}
            name="email"
            type="email"
            placeholder="Type your email"
          />
        </div>

        <div className="email-protection">
          <InfoProtection />
        </div>
      </IonContent>
    </EmailStyles>
  );
};
